import { useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useNavigate } from 'react-router-dom';

import { CasesApi, IDatahubCase } from 'api';
import { ORDER_CREATION_PACKAGE_TYPE } from 'constants/ui/status';
import { useAuth } from 'modules/auth/hooks';
import { DEFAULT_ORDER_CREATION_FORM } from 'modules/v2/order/neworder/CreateNewOrder';
import routes from 'app.routes';
import { createNewOrderStore } from 'stores/createNewOrderStore';

export type Props = {
  caseId?: string;
  packageType: ORDER_CREATION_PACKAGE_TYPE;
  caseReference?: string;
  branchId?: string;
};

type InitialStateProps = {
  caseData: IDatahubCase;
  packageType: ORDER_CREATION_PACKAGE_TYPE;
  reference?: string;
};

export function useStartCreateNewOrder() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setFormData, clearFormData } = createNewOrderStore();

  const startCreateNewOrder = useCallback(
    async (props: Props) => {
      const { caseId, packageType, caseReference = '', branchId } = props;

      // Clear any existing form data first
      clearFormData();

      // If there is a caseId, we fetch its data and prefill the form
      if (caseId) {
        try {
          const caseData = await CasesApi.getDatahubCase(caseId);
          setFormData({
            ...DEFAULT_ORDER_CREATION_FORM,
            address: caseData?.addressObj
              ? { ...caseData.addressObj, id: caseData.id }
              : {
                  city: '',
                  full: '',
                  houseNumber: '',
                  street: '',
                  postNumber: '',
                  appartmentNumber: '',
                  id: '',
                },
            keyPickup: false,
            shouldSendElectronicInvoice: false,
            shouldSendEndUserInvoice: false,
            hasExternalStylist: false,
            property: {
              basementArea: caseData?.property?.basementArea ?? 0,
              livingArea: caseData?.property?.livingArea ?? 0,
              floors: caseData?.property?.floors ?? 0,
              type: caseData?.property?.type ?? '',
            },
            branchId: branchId ?? caseData?.departmentId ?? user?.branches?.[0]?.id ?? '',
            case: caseData ?? { reference: caseId, isArchived: false },
            contactPersons:
              caseData?.contactPersons && caseData.contactPersons.length > 0
                ? caseData.contactPersons
                : [
                    {
                      type: '',
                      name: '',
                      phone: '',
                      phoneAlt: '',
                    },
                  ],
            orderId: '',
            packageType,
            products: [],
            status: 'draft',
          });

          navigate(routes.v2.order.newOrder);
        } catch (error) {
          console.error('Failed to fetch case data for ordering:', error);
        }
      } else {
        // No caseId: start with minimal form data
        setFormData({
          ...DEFAULT_ORDER_CREATION_FORM,
          branchId: branchId ?? user?.branches?.[0]?.id ?? '',
          case: { reference: caseReference, isArchived: false },
          orderId: uuidv4(),
          packageType,
        });

        navigate(routes.v2.order.newOrder);
      }
    },
    [navigate, user?.branches, clearFormData, setFormData]
  );

  return { startCreateNewOrder };
}

export function useInitStateForCreateOrderByRedirectFlow() {
  const { user } = useAuth();
  const { setFormData, clearFormData } = createNewOrderStore();

  const initStateForCreateNewOrder = useCallback(
    async (props: InitialStateProps) => {
      const { caseData, packageType, reference } = props;

      // Clear any existing form data first
      clearFormData();

      setFormData({
        ...DEFAULT_ORDER_CREATION_FORM,
        address: caseData?.addressObj
          ? { ...caseData.addressObj, id: caseData.id }
          : {
              city: '',
              full: '',
              houseNumber: '',
              street: '',
              postNumber: '',
              appartmentNumber: '',
              id: '',
            },
        keyPickup: false,
        shouldSendElectronicInvoice: false,
        shouldSendEndUserInvoice: false,
        hasExternalStylist: false,
        property: {
          basementArea: caseData?.property?.basementArea ?? 0,
          livingArea: caseData?.property?.livingArea ?? 0,
          floors: caseData?.property?.floors ?? 0,
          type: caseData?.property?.type ?? '',
        },
        branchId: caseData?.departmentId ?? user?.branches?.[0]?.id ?? '',
        case: caseData ?? { reference: reference, isArchived: false },
        contactPersons:
          caseData?.contactPersons && caseData.contactPersons.length > 0
            ? caseData.contactPersons
            : [
                {
                  type: '',
                  name: '',
                  phone: '',
                  phoneAlt: '',
                },
              ],
        orderId: uuidv4(),
        packageType,
        products: [],
        status: 'draft',
      });
    },
    [user?.branches, clearFormData, setFormData]
  );

  return { initStateForCreateNewOrder };
}
